import { useEffect, useMemo, useState } from 'react';
import { useDivisionOptions, type OptionsSelectList } from '../state/master';
import { useUserProfile } from '../context/UserProfileContext';
import { getCognitoUserEmail } from '../context/UserContext';
import { useLoginId } from './useLoginId';
import { USER_REGISTERED_EVENT } from '../lib/userRegistrationEvent';
import { shareInFlightRequest } from '../lib/sharedApiRequest';
import {
  fetchUserInfoOnce,
  type UserInfoRow,
} from '../components/common/utils/SearchArea.utils';
import { resolveOwnDivisionOption } from '../utils/divisionOption';

/**
 * 課の選択肢（入力・編集画面の「課」プルダウンと同じリスト）と、ログインユーザー自身の課を返す。
 * - divisionOptions: /get_department を status='N' 除外・label 自然順で並べたもの（useDivisionOptions と共通）
 * - ownDivisionOption: /user-info・profile から解決した自分の課（解決できなければ null）
 * 選択状態は持たない。初期選択の適用は呼び出し側で行う。
 */
export const useOwnDivision = (): {
  divisionOptions: OptionsSelectList[];
  ownDivisionOption: OptionsSelectList | null;
  isReady: boolean;
} => {
  const { divisionOptions } = useDivisionOptions();
  const { profile } = useUserProfile();
  const loginId = useLoginId();

  const [cognitoEmail, setCognitoEmail] = useState('');
  const [users, setUsers] = useState<UserInfoRow[]>([]);
  const [userInfoLoaded, setUserInfoLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const email = String((await getCognitoUserEmail()) ?? '').trim();
      if (!cancelled) setCognitoEmail(email);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await shareInFlightRequest('user-info', fetchUserInfoOnce);
        if (!cancelled) setUsers(Array.isArray(data?.users) ? data.users : []);
      } catch {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setUserInfoLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // ユーザー情報の新規登録後は /user-info を取り直す
  useEffect(() => {
    const onUserRegistered = () => setReloadKey((k) => k + 1);
    window.addEventListener(USER_REGISTERED_EVENT, onUserRegistered);
    return () => window.removeEventListener(USER_REGISTERED_EVENT, onUserRegistered);
  }, []);

  const email = useMemo(() => {
    const profileEmail =
      (profile as any)?.email ?? (profile as any)?.mail_address ?? (profile as any)?.login_id ?? '';
    return String(cognitoEmail || loginId || profileEmail || '').trim();
  }, [cognitoEmail, loginId, profile]);

  const profileDivision = useMemo(
    () => String(((profile as any)?.division ?? profile?.department ?? '') || '').trim(),
    [profile]
  );

  const ownDivisionOption = useMemo(
    () =>
      userInfoLoaded
        ? resolveOwnDivisionOption(divisionOptions, { email, users, profileDivision })
        : null,
    [userInfoLoaded, divisionOptions, email, users, profileDivision]
  );

  return {
    divisionOptions,
    ownDivisionOption,
    isReady: userInfoLoaded && divisionOptions.length > 0,
  };
};
